package no.vareflow.app.domain

/**
 * How many products of a category had a count over time, rebuilt from the audit log
 * (CountEntry · quantity events). Used for the small progress curves on category cards.
 * Pure function: phone and web pass their own audit list.
 */
object ProgressTimeline {

    /** One point: after [time], [counted] product/department entries had a quantity. */
    data class Point(val time: Long, val counted: Int)

    fun build(events: List<AuditEvent>, sessionId: String, category: String, maxPoints: Int = 24): List<Point> {
        val relevant = events.asSequence()
            .filter { it.entityType == "CountEntry" && it.field == "quantity" && it.inventorySessionId == sessionId && it.category == category }
            .sortedBy { it.timestamp }
            .toList()
        if (relevant.isEmpty()) return emptyList()
        val counted = mutableSetOf<String>()
        val points = ArrayList<Point>(relevant.size + 1)
        points += Point(relevant.first().timestamp, 0)
        relevant.forEach { event ->
            if (event.afterValue.isBlank()) counted.remove(event.entityId) else counted.add(event.entityId)
            points += Point(event.timestamp, counted.size)
        }
        return downsample(points, maxPoints)
    }

    /** Keeps the first and last point and evenly spaced points in between. */
    private fun downsample(points: List<Point>, maxPoints: Int): List<Point> {
        if (points.size <= maxPoints || maxPoints < 2) return points
        val step = (points.size - 1).toDouble() / (maxPoints - 1)
        return List(maxPoints) { i -> points[(i * step).toInt().coerceAtMost(points.lastIndex)] }.let { sampled ->
            sampled.dropLast(1) + points.last()
        }
    }

    /**
     * Counting pace: number of quantity changes in each of [buckets] equal time slices between the
     * first and last change (all in one slice when they happened at the same moment).
     */
    fun activity(events: List<AuditEvent>, sessionId: String, category: String, buckets: Int = 16): List<Int> {
        val times = events.asSequence()
            .filter { it.entityType == "CountEntry" && it.field == "quantity" && it.inventorySessionId == sessionId && it.category == category }
            .map { it.timestamp }
            .sorted()
            .toList()
        if (times.isEmpty() || buckets < 1) return emptyList()
        val result = IntArray(buckets)
        val start = times.first()
        val span = times.last() - start
        times.forEach { time ->
            val index = if (span == 0L) buckets - 1 else ((time - start) * buckets / (span + 1)).toInt().coerceIn(0, buckets - 1)
            result[index]++
        }
        return result.toList()
    }
}
